import { useNavigate } from 'react-router-dom'
import { LocationWindow } from '../LocationWindow'
import { useEffect, useMemo, useState } from 'react'
import type { ConsumableUses, InventoryEntry, Loadout, Slot } from '@mezha/api'
import { useEquipMutation, useHeroQuery, useInventoryQuery, useLoadoutQuery, useUnequipMutation } from '../../api/endpoints/hero'
import { useUseItemMutation } from '../../api/endpoints/trade'
import { useWorldQuery } from '../../api/endpoints/world'
import { useCommand } from '../../lib/useCommand'
import { clockText } from '../../lib/time'
import { usePhone } from '../../lib/useSize'
import { useShellMode } from '../../shell/ShellContext'
import { useAppDispatch } from '../../app/store'
import { ui } from '../../app/uiSlice'
import { Emblem } from '../../ui/brand/Emblems'
import { Empty, Failure, Loading } from '../../ui/State'
import { PlankButton, ActionButton, ItemCell, Sheet, cx, rarityClass } from '../../ui'
import { CATEGORY_RU, CATEGORY_SLOT, ItemArt, KIND_EMBLEM, KIND_RU, ResourceBonusNote, gearFacts, modDiff, modList, pct, rarityRu, slotLabel, restoreList, unmetOf, unmetText } from '../common'
import './inventory.css'

const ORDER = ['gear', 'material', 'quest', 'clue', 'consumable', 'cosmetic']

/** `/play/inventory`: the bag by kinds, stacks, rarity, what is equipped and the item card. */
export function Inventory() {
  useShellMode('window')
  const navigate = useNavigate()
  const inv = useInventoryQuery()
  const loadout = useLoadoutQuery()
  const hero = useHeroQuery()
  const world = useWorldQuery()
  const phone = usePhone()
  const dispatch = useAppDispatch()
  const [kind, setKind] = useState<string>('all')
  const [picked, setPicked] = useState<number | null>(null)
  const entries = useMemo(() => inv.data?.entries ?? [], [inv.data])
  const kinds = useMemo(() => ORDER.filter(k => entries.some(e => e.item.kind === k)), [entries])
  const shown = kind === 'all' ? entries : entries.filter(e => e.item.kind === kind)
  const chosen = entries.find(e => e.id === picked) ?? null
  const [consume] = useUseItemMutation()
  const [equip] = useEquipMutation()
  const [unequip] = useUnequipMutation()
  const cmd = useCommand(() => { void inv.refetch(); void hero.refetch(); void loadout.refetch() })
  const [restored, setRestored] = useState<string | null>(null)
  useEffect(() => { setRestored(null); cmd.clear() }, [picked]) // eslint-disable-line react-hooks/exhaustive-deps
  const uses = inv.data?.uses
  const spent = uses ? uses.used >= uses.limit : false
  const inCombat = Boolean(hero.data?.in_combat)
  const drink = async (e: InventoryEntry) => {
    if (!inv.data) return
    setPicked(e.id)
    const r = await cmd.run(() => consume({ expected_version: inv.data!.version, entry_id: e.id, ...(e.item.use?.speedup ? { expected_action_id: world.data?.activity?.id ?? 0 } : {}) }).unwrap())
    if (r) {
      const parts = [r.restored.hp && `+${r.restored.hp} здоровья`, r.restored.mn && `+${r.restored.mn} маны`, r.restored.st && `+${r.restored.st} выносливости`].filter(Boolean)
      setRestored(r.action_completed ? `${r.item.name}: дело завершено, обычная награда получена.` : `${r.item.name}: ${parts.join(', ')}`)
    }
  }
  // Put on and take off right here: no detour to the hero screen (v04).
  const wear = async (e: InventoryEntry) => {
    const slot = wearSlot(e, loadout.data)
    if (!hero.data || !slot) return
    setPicked(e.id)
    const r = await cmd.run(() => equip({ expected_version: hero.data!.version, slot, entry_id: e.id }).unwrap())
    if (r) dispatch(ui.actions.notice(`Надето: ${e.item.name} · ${slotLabel(slot).toLowerCase()}`))
  }
  const takeOff = async (e: InventoryEntry) => {
    const slot = e.equipped_in
    if (!hero.data || !slot) return
    setPicked(e.id)
    const r = await cmd.run(() => unequip({ expected_version: hero.data!.version, slot }).unwrap())
    if (r) dispatch(ui.actions.notice(`Снято: ${e.item.name}`))
  }
  /** The item's main action — the double press on its cell. */
  const main = (e: InventoryEntry) => {
    if (inCombat) return
    if (e.item.kind === 'gear') {
      if (!loadout.data || !hero.data || (!e.equipped_in && (e.item.level_required > hero.data.level || unmetOf(e.item, hero.data.attributes)))) return
      return void (e.equipped_in ? takeOff(e) : wear(e))
    }
    if (e.item.use && (e.item.use.speedup ? !!world.data?.activity : !spent)) return void drink(e)
  }

  const note = (restored || cmd.text) && <p className={cx('inv__note', cmd.text && 'is-error')} role={cmd.text ? 'alert' : 'status'}>{cmd.text ?? restored}</p>
  const card = chosen && (
    <>
      <ItemCard entry={chosen} loadout={loadout.data} uses={chosen.item.use?.speedup ? inv.data?.speedup_uses ?? undefined : uses} activeWork={world.data?.activity && ['fish','gather'].includes(world.data.activity.action) ? world.data.activity.name : undefined} inCombat={inCombat} onWear={() => wear(chosen)} onTakeOff={() => takeOff(chosen)} onUse={() => drink(chosen)} />
      {note}
    </>
  )
  const tabs = [{ id: 'all', label: 'Всё', count: entries.length }, ...kinds.map(k => ({ id: k, label: KIND_RU[k] ?? k, count: entries.filter(e => e.item.kind === k).length }))]

  return (
    <LocationWindow title="Сума" emblem="nav.inventory" idBase="inv" bodyClassName="inv" action={<PlankButton onClick={() => navigate("/play/traders")}>Продать торговцу</PlankButton>}>
      {inv.isLoading && <Loading />}
      {inv.isError && !inv.data && <Failure error={inv.error} retry={() => void inv.refetch()} what="Сума" />}
      {loadout.isError && <Failure error={loadout.error} retry={() => void loadout.refetch()} what="Снаряжение" />}
      {inv.data && entries.length === 0 && (
        <Empty>
          <b>Сума пуста.</b>
          Предметы приносят награды за задания и поручения, добыча в боях и дела на клетках карты — рыбалка и травы.
        </Empty>
      )}
      {inv.data && entries.length > 0 && (
        <>
          <div className={cx('inv__rail', phone && 'chips')} role="tablist" aria-label="Разделы сумы">
            {tabs.map(t => (
              <button key={t.id} type="button" role="tab" aria-selected={kind === t.id} className={cx('rtab', kind === t.id && 'is-active')} onClick={() => setKind(t.id)}>
                <Emblem name={t.id === 'all' ? 'nav.inventory' : KIND_EMBLEM[t.id] ?? 'nav.inventory'} />{t.label}<b>{t.count}</b>
              </button>
            ))}
          </div>
          <div className="inv__grid" role="list" aria-label="Предметы">
            {shown.map(e => (
              <div role="listitem" key={e.id}>
                <ItemCell selected={picked === e.id} onSelect={() => setPicked(picked === e.id ? null : e.id)} onActivate={() => { setPicked(e.id); main(e) }}
                  item={{ art: <ItemArt item={e.item} size={46} />, name: e.item.name, rarity: e.item.rarity, quantity: e.quantity, equipped: Boolean(e.equipped_in) }} />
              </div>
            ))}
          </div>
          {!phone && (
            <div className="inv__card">
              <div className="eyebrow">Предмет</div>
              {card ?? <p className="note">Выберите предмет, чтобы увидеть его свойства.</p>}
              <div className="inv__foot"><Emblem name="nav.inventory" size={16} />{entries.length} {entries.length === 1 ? 'запись' : 'записей'} · золотая метка — надето · двойное нажатие — надеть или выпить</div>
            </div>
          )}
          {phone && (
            <Sheet open={Boolean(chosen)} title={chosen?.item.name ?? ''} emblem="nav.inventory" onClose={() => setPicked(null)}>
              {card}
            </Sheet>
          )}
        </>
      )}
    </LocationWindow>
  )
}

/** A brew is drunk; a soup or bread is eaten. */
const verbFor = (e: InventoryEntry): string => e.item.use?.speedup ? 'Завершить дело' : e.item.premium_bound ? 'Применить' : (e.item.use?.st ? 'Съесть' : 'Выпить')

/** Where the item goes: its own slot if worn, the first free charm slot for a charm, else its category slot. */
function wearSlot(entry: InventoryEntry, loadout: Loadout | undefined): Slot | null {
  const it = entry.item
  if (it.kind !== 'gear') return null
  if (entry.equipped_in) return entry.equipped_in
  const first = CATEGORY_SLOT[it.slot_category] ?? null
  if (it.slot_category !== 'accessory' || !loadout) return first
  return loadout.slots.find(s => s.categories.includes('accessory') && !s.entry)?.slot ?? first
}

type CardProps = {
  activeWork?: string
  entry: InventoryEntry; loadout: Loadout | undefined; uses: ConsumableUses | undefined; inCombat: boolean
  onWear: () => Promise<unknown>; onTakeOff: () => Promise<unknown>; onUse: () => Promise<unknown>
}

function ItemCard({ entry, loadout, uses, activeWork, inCombat, onWear, onTakeOff, onUse }: CardProps) {
  const it = entry.item
  const attrs = loadout?.hero.attributes
  const unmet = unmetOf(it, attrs)
  const lowLevel = Boolean(loadout && it.level_required > loadout.hero.level)
  const target = wearSlot(entry, loadout)
  const worn = target ? loadout?.slots.find(s => s.slot === target)?.entry ?? null : null
  const diff = worn && worn.id !== entry.id ? modDiff(worn.item.modifiers, it.modifiers) : []
  const kindLine = it.kind === 'gear' ? `${rarityRu(it.rarity)} · ${CATEGORY_RU[it.slot_category] ?? it.slot_category}` : `${rarityRu(it.rarity)} · ${(KIND_RU[it.kind] ?? it.kind).toLowerCase()}`
  return (
    <div className={cx('itemcard parch', rarityClass(it.rarity))}>
      <div className="itemcard__head">
        <ItemArt item={it} size={58} />
        <div><b>{it.name}</b><span className="rarity-line">{kindLine}{it.level_required > 1 ? ` · ур. ${it.level_required}` : ''}</span></div>
      </div>
      {it.description && <p className="itemcard__text">{it.description}</p>}
      <div className="mods">
        {modList(it.modifiers).map(m => <Pair key={m.key} a={m.label} b={m.text} />)}
        {gearFacts(it, attrs).map(f => <Pair key={f.key} a={f.label} b={f.text} down={f.down} />)}
        {restoreList(it.use).map(u => <Pair key={u.key} a={u.label} b={u.text} />)}
        {it.use && uses && <Pair a={it.use.speedup ? 'Сегодня ускорено дел' : 'Сегодня выпито и съедено'} b={`${uses.used} из ${uses.limit}`} down={uses.used >= uses.limit} ink={uses.used < uses.limit} />}
        {entry.quantity > 1 && <Pair a="В стопке" b={`${entry.quantity} из ${it.stack_limit}`} ink />}
        {entry.equipped_in && <Pair a="Надето" b={slotLabel(entry.equipped_in)} ink />}
        {worn && worn.id !== entry.id && <Pair a="Сейчас в ячейке" b={worn.item.name} ink />}
        {diff.map(d => <Pair key={d.key} a={`При замене: ${d.label.toLowerCase()}`} b={d.key === 'crit_chance_bp' ? `${pct(d.from)} → ${pct(d.to)}` : `${d.from} → ${d.to}`} down={d.delta < 0} />)}
      </div>
      {it.kind === 'gear' && <ResourceBonusNote modifiers={it.modifiers} />}
      {it.premium_bound && <p className="note">Личный расходник. Нельзя продать, передать или сдать в поручение.</p>}
      {target && (
        <div className="actions">
          {entry.equipped_in
            ? <ActionButton locked={inCombat} onPress={onTakeOff}>Снять</ActionButton>
            : <ActionButton gold locked={inCombat || !loadout || lowLevel || Boolean(unmet)} onPress={onWear}>{!loadout ? 'Загружаем снаряжение…' : lowLevel ? `Нужен уровень ${it.level_required}` : unmet ? `Не надеть: ${unmetText(unmet, attrs)}` : `Надеть · ${slotLabel(target).toLowerCase()}`}</ActionButton>}
        </div>
      )}
      {target && inCombat && <p className="note">Идёт бой: снаряжение меняется после боя.</p>}
      {it.use && uses && (
        <>
          {uses.used >= uses.limit && <p className="note">На сегодня хватит: не больше {uses.limit} {it.use.speedup ? 'ускорителей' : 'отваров и блюд'} в сутки. Снова можно в {clockText(uses.reset_at)}.</p>}
          {it.use.speedup && <p className="note">{activeWork ? `Завершить: ${activeWork}. Обычная награда, без дополнительной попытки.` : 'Сначала начните рыбалку или сбор на карте.'}</p>}
          {inCombat && <p className="note">Применить можно после боя.</p>}
          <div className="actions">
            <ActionButton gold locked={inCombat || uses.used >= uses.limit || (!!it.use.speedup && !activeWork)} onPress={onUse}>{uses.used >= uses.limit ? 'На сегодня хватит' : `${verbFor(entry)} · осталось ${uses.limit - uses.used} на сегодня`}</ActionButton>
          </div>
        </>
      )}
    </div>
  )
}

function Pair({ a, b, down, ink }: { a: string; b: string; down?: boolean; ink?: boolean }) {
  return <><span>{a}</span><u className={cx(down && 'down', ink && 'ink')}>{b}</u></>
}
